import { useCallback, useRef } from 'react';
import { Autocomplete } from '../Autocomplete';
import { useOrgLookup, type OrgLookupValue } from '../../hooks/useOrgLookup';

interface OrgComboboxProps {
  id: string;
  category: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  value?: string;
  onValueChange: (value: string) => void;
  error?: string;
  allowFreeText?: boolean;
}

export function OrgCombobox({
  id,
  category,
  label,
  required = false,
  placeholder,
  value,
  onValueChange,
  error,
  allowFreeText = true,
}: OrgComboboxProps) {
  const { searchValues, createValue } = useOrgLookup(category);
  const lastSelectedRef = useRef<string | null>(null);

  const getLabel = useCallback(
    (item: OrgLookupValue) => item.label ?? item.value,
    [],
  );

  const handleSelect = useCallback(
    (item: OrgLookupValue) => {
      lastSelectedRef.current = item.value;
      onValueChange(item.value);
    },
    [onValueChange],
  );

  const handleInputChange = useCallback(
    (text: string) => {
      if (!allowFreeText) return;
      lastSelectedRef.current = null;
      onValueChange(text);
    },
    [allowFreeText, onValueChange],
  );

  const handleBlurSave = useCallback(
    (text: string) => {
      if (!allowFreeText || !text.trim()) return;
      // If the text came from selecting a dropdown item, skip auto-create
      if (lastSelectedRef.current === text) return;
      // Silently persist the free-text value for future lookups
      createValue(text).catch(() => {
        /* silent — best effort */
      });
    },
    [allowFreeText, createValue],
  );

  // Build an initialItem so the Autocomplete shows the current value
  const initialItem: OrgLookupValue | null = value
    ? ({ value, label: null } as unknown as OrgLookupValue)
    : null;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-navy mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <Autocomplete<OrgLookupValue>
        id={id}
        fetchResults={searchValues}
        onSelect={handleSelect}
        getLabel={getLabel}
        getKey={(item) => item.id ?? item.value}
        initialItem={initialItem}
        placeholder={placeholder ?? `Search ${label.toLowerCase()}...`}
        minChars={0}
        onInputChange={(text) => {
          handleInputChange(text);
          // On blur the Autocomplete also fires onInputChange — use it to auto-save
          handleBlurSave(text);
        }}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
